import {useEffect, useRef, useState} from "react";
import type {CSSProperties, ReactNode} from "react";
import {useTranslation} from "react-i18next";
import type {TFunction} from "i18next";
import Button from "@mui/material/Button";
import type {PipelineActionState, PipelineNodeStatus, PipelineStageState} from "@calendall/shared";
import {usePipelines, usePipelineStatus} from "../api/usePipelines";
import {PageHeader} from "../components/ui/PageHeader";
import {PanCanvas} from "../components/ui/PanCanvas";
import type {PanCanvasHandle} from "../components/ui/PanCanvas";
import {colors, fonts} from "../theme/tokens";
import "./PipelinePage.css";

// One node per CodePipeline stage, left to right in stage order; clicking a node opens its actions.
const NODE_WIDTH = 128;
const NODE_HEIGHT = 46;
const COLUMN_GAP = 220;
const nodeLeft = (index: number): number => index * COLUMN_GAP;

const TOKEN_VARIABLES: CSSProperties = {
    "--pl-good": colors.success,
    "--pl-bad": colors.error,
    "--pl-warn": colors.warning,
    "--pl-accent": colors.primary,
    "--pl-ink": colors.ink,
    "--pl-ink-soft": colors.body,
    "--pl-ink-muted": colors.muted,
    "--pl-ink-dim": colors.faint,
    "--pl-line": colors.cardBorder,
    "--pl-line-strong": colors.inputBorder,
    "--pl-surface": colors.card,
    "--pl-background": colors.canvas,
    "--pl-font-mono": fonts.mono,
};

const WIRE_CLASS: Record<PipelineNodeStatus, string> = {
    succeeded: "good",
    failed: "bad",
    in_progress: "running",
    waiting_approval: "warn",
    stopped: "stopped",
    pending: "pending",
};

const STATUS_LABEL_KEY: Record<PipelineNodeStatus, string> = {
    succeeded: "PIPELINE_STATUS_SUCCEEDED",
    failed: "PIPELINE_STATUS_FAILED",
    in_progress: "PIPELINE_STATUS_IN_PROGRESS",
    waiting_approval: "PIPELINE_STATUS_WAITING_APPROVAL",
    stopped: "PIPELINE_STATUS_STOPPED",
    pending: "PIPELINE_STATUS_PENDING",
};

/** Named for assistive technology only when no adjacent text already states the status. */
function StatusIcon({status, label}: {status: PipelineNodeStatus; label?: string}) {
    return (
            <span className={`pl-stat s-${status}`} role={label ? "img" : undefined} aria-label={label}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className={status === "in_progress" ? "pl-spin" : undefined} aria-hidden="true">
                    {status === "succeeded" && <path d="M20 6 9 17l-5-5" />}
                    {status === "failed" && <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />}
                    {status === "in_progress" && <path d="M21 12a9 9 0 1 1-9-9" />}
                    {status === "waiting_approval" && (
                            <>
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3 3" />
                            </>
                    )}
                    {status === "stopped" && <rect x="7" y="7" width="10" height="10" rx="1.5" />}
                    {status === "pending" && <circle cx="12" cy="12" r="9" strokeDasharray="3 3" />}
                </svg>
            </span>
    );
}

function ActionCard({action, t}: {action: PipelineActionState; t: TFunction}) {
    return (
            <div className={`pl-act s-${action.status}`}>
                <div className="pl-act-row">
                    <StatusIcon status={action.status} label={t(STATUS_LABEL_KEY[action.status])} />
                    <span className="pl-act-name">{action.name}</span>
                </div>
                {action.category && <div className="pl-act-category">{action.category}</div>}
                {action.errorMessage && <div className="pl-act-error">{action.errorMessage}</div>}
                {action.externalExecutionUrl && (
                        <a className="pl-act-link" href={action.externalExecutionUrl} target="_blank" rel="noreferrer">
                            {t("PIPELINE_VIEW_IN_AWS")}
                        </a>
                )}
            </div>
    );
}

interface DetailAnchor {
    stageIndex: number;
    rect: DOMRect;
    /** The node that opened the detail, which gets the focus back when Escape closes it. */
    trigger: HTMLElement;
}

const DETAIL_WIDTH = 300;
const DETAIL_GAP = 8;
const VIEWPORT_MARGIN = 12;
// Below this much room under the node, the detail opens above it if there is more room there.
const DETAIL_MIN_HEIGHT_BELOW = 240;

/** Keeps the detail inside the window: it opens below the node, or above it when that leaves more room, and scrolls. */
function detailPosition(anchor: DOMRect): CSSProperties {
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, window.innerWidth - VIEWPORT_MARGIN - DETAIL_WIDTH));
    const roomBelow = window.innerHeight - anchor.bottom - DETAIL_GAP - VIEWPORT_MARGIN;
    const roomAbove = anchor.top - DETAIL_GAP - VIEWPORT_MARGIN;
    if (roomBelow < DETAIL_MIN_HEIGHT_BELOW && roomAbove > roomBelow) {
        return {left, bottom: window.innerHeight - anchor.top + DETAIL_GAP, maxHeight: roomAbove};
    }
    return {left, top: anchor.bottom + DETAIL_GAP, maxHeight: Math.max(roomBelow, 0)};
}

function StageDetail({stage, anchor, onClose, t}: {
    stage: PipelineStageState;
    anchor: DOMRect;
    onClose: (returnFocus: boolean) => void;
    t: TFunction;
}) {
    const ref = useRef<HTMLDivElement>(null);

    // Opening or switching stages moves the focus into the detail, so keyboard and screen-reader users land on it.
    useEffect(() => {
        ref.current?.focus({preventScroll: true});
    }, [stage.name]);

    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === "Escape") onClose(true);
        };
        // A press on a node switches the detail instead, so only presses elsewhere close it.
        const onDown = (event: MouseEvent): void => {
            const target = event.target;
            if (!(target instanceof Node) || ref.current?.contains(target)) return;
            if (target instanceof Element && target.closest(".pl-node")) return;
            onClose(false);
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
        };
    }, [onClose]);

    return (
            <div className="pl-detail" ref={ref} style={{...TOKEN_VARIABLES, ...detailPosition(anchor), width: DETAIL_WIDTH}} role="dialog" aria-label={stage.name} tabIndex={-1}>
                <h4>
                    <StatusIcon status={stage.status} />
                    {stage.name}
                </h4>
                <p className="pl-detail-sub">{t(STATUS_LABEL_KEY[stage.status])}</p>
                {stage.actions.map((action) => <ActionCard key={action.name} action={action} t={t} />)}
            </div>
    );
}

function formatAgo(retrievedAt: string, t: TFunction): string {
    const seconds = Math.max(0, Math.round((Date.now() - new Date(retrievedAt).getTime()) / 1000));
    if (seconds < 60) return t("PIPELINE_AGO_SECONDS", {count: seconds});
    return t("PIPELINE_AGO_MINUTES", {count: Math.round(seconds / 60)});
}

function GraphBoard({stages, openStageIndex, onSelect, t}: {
    stages: PipelineStageState[];
    openStageIndex: number | undefined;
    onSelect: (index: number, trigger: HTMLElement) => void;
    t: TFunction;
}) {
    const width = Math.max(NODE_WIDTH, (stages.length - 1) * COLUMN_GAP + NODE_WIDTH);
    const middleY = NODE_HEIGHT / 2;

    return (
            <div className="pl-graph" style={{width, height: NODE_HEIGHT}}>
                <svg className="pl-wires" width={width} height={NODE_HEIGHT} viewBox={`0 0 ${width} ${NODE_HEIGHT}`} aria-hidden="true">
                    {stages.slice(0, -1).map((stage, index) => (
                            <path
                                    key={stage.name}
                                    className={`pl-wire ${WIRE_CLASS[stage.status]}`}
                                    d={`M${nodeLeft(index) + NODE_WIDTH} ${middleY} L${nodeLeft(index + 1)} ${middleY}`}
                            />
                    ))}
                </svg>
                {stages.map((stage, index) => (
                        <button
                                key={stage.name}
                                type="button"
                                className={`pl-node s-${stage.status}`}
                                style={{left: nodeLeft(index), top: 0}}
                                aria-label={`${stage.name}: ${t(STATUS_LABEL_KEY[stage.status])}`}
                                aria-haspopup="dialog"
                                aria-expanded={openStageIndex === index}
                                onClick={(event) => onSelect(index, event.currentTarget)}
                        >
                            {stage.name}
                        </button>
                ))}
            </div>
    );
}

function OverlayIcon({bad, children}: {bad?: boolean; children: ReactNode}) {
    return (
            <span className={`pl-overlay-icon${bad ? " is-bad" : ""}`}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    {children}
                </svg>
            </span>
    );
}

/** The live state of the deploy pipeline as a pannable board of its stages; read-only. */
export function PipelinePage() {
    const {t} = useTranslation();
    const pipelines = usePipelines();
    const target = pipelines.state.kind === "loaded" ? pipelines.state.targets[0] : undefined;
    const pipelineStatus = usePipelineStatus(target?.name);
    const [detail, setDetail] = useState<DetailAnchor | null>(null);
    const canvasRef = useRef<PanCanvasHandle>(null);

    useEffect(() => {
        document.title = t("PIPELINE_PAGE_TITLE");
    }, [t]);

    const notConfigured = pipelines.state.kind === "loaded" && pipelines.state.targets.length === 0;
    const status = pipelineStatus.status;
    const statusLoading = target !== undefined && !status && !pipelineStatus.failed;
    const statusFailed = target !== undefined && !status && pipelineStatus.failed;
    const neverRan = status !== undefined && status.execution === null;
    const board = status?.execution ? status : undefined;
    const revision = board?.execution?.sourceRevisions[0];
    const refreshing = board !== undefined && pipelineStatus.fetching && !pipelineStatus.failed;
    const stale = board !== undefined && pipelineStatus.failed;
    const boardVisible = board !== undefined;

    // The canvas fits once on mount, before the status arrives, so it fits again when the board first appears.
    useEffect(() => {
        if (!boardVisible) return;
        const frame = requestAnimationFrame(() => canvasRef.current?.fit());
        return () => cancelAnimationFrame(frame);
    }, [boardVisible]);

    const detailStage = detail ? (board?.stages[detail.stageIndex] ?? null) : null;

    const retry = (): void => {
        if (pipelines.state.kind === "failed") pipelines.reload();
        else pipelineStatus.retry();
    };

    return (
            <>
                <PageHeader title={t("NAV_PIPELINE")} subtitle={t("PIPELINE_SUBTITLE")} />

                <div className="pl-wrap" style={TOKEN_VARIABLES}>
                    <div className="pl-execbar">
                        {board && (
                                <>
                                    <span className="pl-exec-name">{board.pipelineName}</span>
                                    {revision && (
                                            <div className="pl-commit">
                                                <span className="sha">{revision.revisionId.slice(0, 7)}</span>
                                                {revision.summary && <span className="message">{revision.summary}</span>}
                                            </div>
                                    )}
                                    <div className={`pl-fresh${stale ? " is-stale" : ""}`}>
                                        {refreshing ? (
                                                <>
                                                    <span className="pl-refresh-spin" />
                                                    <span>{t("PIPELINE_REFRESHING")}</span>
                                                </>
                                        ) : (
                                                <>
                                                    <span className="pl-fresh-dot" />
                                                    <span>{t(stale ? "PIPELINE_AS_OF_RECONNECTING" : "PIPELINE_AS_OF", {ago: formatAgo(board.retrievedAt, t)})}</span>
                                                </>
                                        )}
                                    </div>
                                </>
                        )}
                    </div>

                    <div className="pl-canvas-wrap">
                        <PanCanvas
                                ref={canvasRef}
                                grid
                                hud={boardVisible}
                                frozen={!boardVisible}
                                hint={t("PIPELINE_PAN_HINT")}
                                labels={{zoomOut: t("PIPELINE_ZOOM_OUT"), zoomIn: t("PIPELINE_ZOOM_IN"), fit: t("PIPELINE_FIT")}}
                                layerStyle={{padding: 48}}
                                style={{flex: 1, minHeight: 0}}
                        >
                            {board && (
                                    <GraphBoard
                                            stages={board.stages}
                                            openStageIndex={detail?.stageIndex}
                                            onSelect={(stageIndex, trigger) => setDetail({stageIndex, rect: trigger.getBoundingClientRect(), trigger})}
                                            t={t}
                                    />
                            )}
                        </PanCanvas>

                        {stale && <div className="pl-stale-pill">{t("PIPELINE_STALE_WARNING")}</div>}

                        {(pipelines.state.kind === "loading" || statusLoading) && (
                                <div className="pl-overlay">
                                    <div className="pl-skeleton-row">
                                        <div className="pl-skeleton-node" />
                                        <div className="pl-skeleton-node" />
                                        <div className="pl-skeleton-node" />
                                    </div>
                                    <p>{t("PIPELINE_LOADING")}</p>
                                </div>
                        )}

                        {(pipelines.state.kind === "failed" || statusFailed) && (
                                <div className="pl-overlay">
                                    <OverlayIcon bad>
                                        <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                                    </OverlayIcon>
                                    <h3>{t("PIPELINE_ERROR_TITLE")}</h3>
                                    <p>{t("PIPELINE_ERROR_BODY")}</p>
                                    <Button variant="contained" onClick={retry}>{t("PIPELINE_RETRY")}</Button>
                                </div>
                        )}

                        {neverRan && (
                                <div className="pl-overlay">
                                    <OverlayIcon>
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 8v4" />
                                        <path d="M12 16h.01" />
                                    </OverlayIcon>
                                    <h3>{t("PIPELINE_EMPTY_TITLE")}</h3>
                                    <p>{t("PIPELINE_EMPTY_BODY")}</p>
                                </div>
                        )}

                        {notConfigured && (
                                <div className="pl-overlay">
                                    <OverlayIcon>
                                        <rect x="4" y="10" width="16" height="10" rx="2" />
                                        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                                    </OverlayIcon>
                                    <h3>{t("PIPELINE_UNAVAILABLE_TITLE")}</h3>
                                    <p>{t("PIPELINE_UNAVAILABLE_BODY")}</p>
                                </div>
                        )}
                    </div>
                </div>

                {detailStage && detail && (
                        <StageDetail
                                stage={detailStage}
                                anchor={detail.rect}
                                onClose={(returnFocus) => {
                                    if (returnFocus) detail.trigger.focus();
                                    setDetail(null);
                                }}
                                t={t}
                        />
                )}
            </>
    );
}
